
import React, { useState } from "react";

interface TruckInfoCardProps {
  vin: string | null;
  gpsTimestamp: string | null;
  address: string | null;
  caseNumber: string;
  isDistanceCalculated: boolean;
  latitude?: number | null;
  longitude?: number | null;
  // NEW: callback when user saves manual coordinates
  onManualGpsUpdate?: (lat: number, lng: number) => void;
}

const label: React.CSSProperties = {
  display: "block",
  marginTop: "0.5rem",
  marginBottom: "0.25rem",
  color: "#002663",
  fontWeight: 700,
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "0.4rem 0.5rem",
  border: "1px solid #d9d9d9",
  borderRadius: 4,
  fontSize: 13,
  boxSizing: "border-box",
};

const row: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "0.5rem",
};

const btnRow: React.CSSProperties = {
  display: "flex",
  gap: "0.5rem",
  marginTop: "0.6rem",
};

const primaryBtn: React.CSSProperties = {
  padding: "0.45rem 0.75rem",
  border: "1px solid #002663",
  borderRadius: 4,
  background: "#002663",
  color: "#fff",
  fontSize: 13,
  cursor: "pointer",
};

const ghostBtn: React.CSSProperties = {
  padding: "0.45rem 0.75rem",
  border: "1px solid #d9d9d9",
  borderRadius: 4,
  background: "#fff",
  color: "#002663",
  fontSize: 13,
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  color: "#b00020",
  fontSize: 12,
  marginTop: "0.25rem",
};

const TruckInfoCard: React.FC<TruckInfoCardProps> = ({
  vin,
  gpsTimestamp,
  address,
  caseNumber,
  isDistanceCalculated,
  latitude = null,
  longitude = null,
  onManualGpsUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [latInput, setLatInput] = useState<string>(
    typeof latitude === "number" && isFinite(latitude) ? String(latitude) : ""
  );
  const [lngInput, setLngInput] = useState<string>(
    typeof longitude === "number" && isFinite(longitude) ? String(longitude) : ""
  );
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setLatInput(typeof latitude === "number" ? String(latitude) : "");
    setLngInput(typeof longitude === "number" ? String(longitude) : "");
    setError(null);
  };

  const validate = (lat: number, lng: number) =>
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

  const onSave = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (Number.isNaN(lat) || Number.isNaN(lng) || !validate(lat, lng)) {
      setError(
        "Invalid coordinates. Latitude must be between -90 and 90, longitude between -180 and 180."
      );
      return;
    }
    setError(null);
    onManualGpsUpdate?.(lat, lng);
    setIsEditing(false);
  };

  const onCancel = () => {
    resetForm();
    setIsEditing(false);
  };

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #d9d9d9",
        borderRadius: 8,
        padding: "0.85rem 1rem",
        marginBottom: "1rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        fontFamily: "Roboto, Arial, sans-serif",
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      {/* <h3 style={{ fontSize: 14, marginBottom: "0.5rem", color: "#002663", fontWeight: 700 }}>
        Truck Info
      </h3> */}
      <p style={{ margin: 0 }}>
        <strong style={{ color: "#002663" }}>Case Number:</strong> {caseNumber}
      </p>
      <p style={{ margin: "0.4rem 0 0" }}>
        <strong style={{ color: "#002663" }}>VIN:</strong> {vin ?? "Unknown"}
      </p>
      <p style={{ margin: "0.4rem 0 0" }}>
        <strong style={{ color: "#002663" }}>GPS Timestamp:</strong>{" "}
        {gpsTimestamp
          ? (() => {
            const d = new Date(gpsTimestamp);
            const pad = (n: number) => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
          })()
          : "Unknown"}
      </p>
      <p style={{ margin: "0.4rem 0 0" }}>
        <strong style={{ color: "#002663" }}>Address:</strong> {address ?? "Loading..."}
      </p>

      {!isEditing ? (
        <>
          <p style={{ margin: "0.4rem 0 0" }}>
            <strong style={{ color: "#002663" }}>Truck position (lat, lng):</strong>{" "}
            {typeof latitude === "number" && typeof longitude === "number" && isFinite(latitude) && isFinite(longitude)
              ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
              : "N/A"}
          </p>
          <div style={btnRow}>
            <button
              type="button"
              style={ghostBtn}
              aria-label="Edit GPS"
              onClick={() => setIsEditing(true)}
            >
              Edit GPS
            </button>
          </div>
        </>
      ) : (
        <div style={{ marginTop: "0.6rem" }}>
          <div style={row}>
            <div>
              <label htmlFor="lat" style={label}>Latitud</label>
              <input
                id="lat"
                type="number"
                step="any"
                placeholder="t.ex. 59.3293"
                value={latInput}
                onChange={(e) => setLatInput(e.target.value)}
                style={input}
              />
            </div>
            <div>
              <label htmlFor="lng" style={label}>Longitud</label>
              <input
                id="lng"
                type="number"
                step="any"
                placeholder="t.ex. 18.0686"
                value={lngInput}
                onChange={(e) => setLngInput(e.target.value)}
                style={input}
              />
            </div>
          </div>
          {error && <div style={errorStyle}>{error}</div>}
          <div style={btnRow}>
            <button type="button" style={primaryBtn} onClick={onSave}>Save</button>
            <button type="button" style={ghostBtn} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TruckInfoCard;
